// Lightweight client-side PIN hashing using Web Crypto SubtleCrypto.
// Why not bcrypt? Pure-JS bcrypt adds ~20KB+ and is slow in browsers;
// PBKDF2-SHA256 via the native crypto.subtle is faster, stronger and dependency-free.
//
// Stored value format:
//   "pbkdf2$<iterations>$<saltHex>$<hashHex>"
//
// A legacy plaintext PIN (e.g. "1234") is accepted as a fallback so existing shops
// keep working — the moment that PIN is verified, the caller can opt to upgrade.

const ITERATIONS = 120_000;
const HASH_LEN_BYTES = 32; // 256 bits

function toHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
function fromHex(hex) {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

async function pbkdf2(pin, salt, iterations = ITERATIONS) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(String(pin)),
    { name: "PBKDF2" },
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt,
      iterations,
      hash: "SHA-256",
    },
    keyMaterial,
    HASH_LEN_BYTES * 8
  );
  return new Uint8Array(bits);
}

export function isHashed(stored) {
  return typeof stored === "string" && stored.startsWith("pbkdf2$");
}

export async function hashPin(pin) {
  if (!pin) throw new Error("PIN required");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await pbkdf2(pin, salt);
  return `pbkdf2$${ITERATIONS}$${toHex(salt)}$${toHex(hash)}`;
}

// Constant-time compare for hex strings of equal length.
function constantTimeEqualHex(a, b) {
  if (typeof a !== "string" || typeof b !== "string") return false;
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export async function verifyPin(input, stored) {
  if (!stored) return false;
  // Plaintext fallback — used until we migrate the row to a hash.
  if (!isHashed(stored)) {
    return String(input) === String(stored);
  }
  const parts = stored.split("$");
  if (parts.length !== 4 || parts[0] !== "pbkdf2") return false;
  const iters = parseInt(parts[1], 10) || ITERATIONS;
  const salt = fromHex(parts[2]);
  const expected = parts[3];
  const got = toHex(await pbkdf2(input, salt, iters));
  return constantTimeEqualHex(got, expected);
}
